import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Jerarquizacion {
  _id?: string
  codigo: string
  nombre_microcuenca: string
  municipio: string
  jerarquia: number
  prioridad: string
  ponderacion_final: number
  superficie_microcuenca?: number
  superficie_agricola?: number
  densidad_poblacion?: number
  precipitacion?: number
  temperatura?: number
  deforestacion?: number
  erosion?: number
  createdAt?: string
  updatedAt?: string
  creator?: string
}

interface JerarquizacionRow {
  CODIGO: string
  MICROCUENCA: string
  MUNICIPIO: string
  JERARQUIA: string
  PRIORIDAD: string
  'PONDERACION FINAL': string
  'SUPERFICIE MICROCUENCA': string
  'SUPERFICIE AGRICOLA': string
  'DENSIDAD DE POBLACION': string
  PRECIPITACION: string
  TEMPERATURA: string
  DEFORESTACION: string
  EROSION: string
}

export const useJerarquizacionCRUD = () => {
  const [jerarquizacion, setJerarquizacion] = useState<Jerarquizacion[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchJerarquizacion = useCallback(async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.jerarquizacion.list({
        sort: { ponderacion_final: -1 }
      })
      setJerarquizacion(response.list || [])
    } catch (error: any) {
      console.error('Error al cargar jerarquización:', error)
      toast.error('Error al cargar los datos de jerarquización')
    } finally {
      setLoading(false)
    }
  }, [])

  const createJerarquizacion = async (data: Omit<Jerarquizacion, '_id'>) => {
    try {
      const newRecord = await lumi.entities.jerarquizacion.create({
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'user'
      })
      setJerarquizacion(prev => [newRecord, ...prev])
      toast.success('Registro de jerarquización creado exitosamente')
      return newRecord
    } catch (error: any) {
      console.error('Error al crear registro de jerarquización:', error)
      toast.error('Error al crear el registro')
      throw error
    }
  }

  const updateJerarquizacion = async (id: string, updates: Partial<Jerarquizacion>) => {
    try {
      const updatedRecord = await lumi.entities.jerarquizacion.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      setJerarquizacion(prev => prev.map(j => j._id === id ? updatedRecord : j))
      toast.success('Registro actualizado exitosamente')
      return updatedRecord
    } catch (error: any) {
      console.error('Error al actualizar registro:', error)
      toast.error('Error al actualizar el registro')
      throw error
    }
  }

  const deleteJerarquizacion = async (id: string) => {
    try {
      await lumi.entities.jerarquizacion.delete(id)
      setJerarquizacion(prev => prev.filter(j => j._id !== id))
      toast.success('Registro eliminado exitosamente')
    } catch (error: any) {
      console.error('Error al eliminar registro:', error)
      toast.error('Error al eliminar el registro')
      throw error
    }
  }

  const processTXTData = (txtContent: string): Omit<Jerarquizacion, '_id'>[] => {
    const lines = txtContent.trim().split('\n')
    if (lines.length < 2) return []
    
    const headers = lines[0].split('\t')
    const data: Omit<Jerarquizacion, '_id'>[] = []
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t')
      if (values.length < headers.length) continue
      
      const row: any = {}
      headers.forEach((header, index) => {
        row[header.trim()] = values[index]?.trim() || ''
      })
      
      data.push({
        codigo: row.CODIGO || '',
        nombre_microcuenca: row.MICROCUENCA || '',
        municipio: row.MUNICIPIO || '',
        jerarquia: parseInt(row.JERARQUIA) || 0,
        prioridad: row.PRIORIDAD || '',
        ponderacion_final: parseFloat(row['PONDERACION FINAL']) || 0,
        superficie_microcuenca: parseFloat(row['SUPERFICIE MICROCUENCA']) || 0,
        superficie_agricola: parseFloat(row['SUPERFICIE AGRICOLA']) || 0,
        densidad_poblacion: parseFloat(row['DENSIDAD DE POBLACION']) || 0,
        precipitacion: parseFloat(row.PRECIPITACION) || 0,
        temperatura: parseFloat(row.TEMPERATURA) || 0,
        deforestacion: parseFloat(row.DEFORESTACION) || 0,
        erosion: parseFloat(row.EROSION) || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'txt_import'
      })
    }
    
    return data
  }

  const uploadTXTFromURL = async (url: string) => {
    setUploading(true)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Error al descargar el archivo')
      }
      const txtContent = await response.text()
      
      const processedData = processTXTData(txtContent)
      
      if (processedData.length === 0) {
        toast.error('No se encontraron datos válidos en el archivo')
        return
      }

      const batchSize = 10
      let successCount = 0
      
      for (let i = 0; i < processedData.length; i += batchSize) {
        const batch = processedData.slice(i, i + batchSize)
        
        try {
          const promises = batch.map(data => 
            lumi.entities.jerarquizacion.create(data)
          )
          await Promise.all(promises)
          successCount += batch.length
        } catch (batchError) {
          console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} registros de jerarquización importados exitosamente`)
        await fetchJerarquizacion()
      } else {
        toast.error('No se pudieron importar los registros')
      }
    } catch (error) {
      console.error('Error en carga de TXT desde URL:', error)
      toast.error('Error al cargar el archivo desde la URL')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      const ids = jerarquizacion.map(j => j._id!).filter(Boolean)
      if (ids.length > 0) {
        const batchSize = 20
        let deletedCount = 0
        
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize)
          try {
            await lumi.entities.jerarquizacion.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setJerarquizacion([])
        toast.success(`${deletedCount} registros de jerarquización eliminados exitosamente`)
      }
    } catch (error: any) {
      console.error('Error al limpiar datos de jerarquización:', error)
      toast.error('Error al limpiar los datos')
      throw error
    }
  }

  const removeDuplicates = async () => {
    try {
      const codigoMap = new Map<string, Jerarquizacion[]>()
      
      jerarquizacion.forEach(j => {
        if (!codigoMap.has(j.codigo)) {
          codigoMap.set(j.codigo, [])
        }
        codigoMap.get(j.codigo)!.push(j)
      })
      
      const idsToDelete: string[] = []
      codigoMap.forEach((items, codigo) => {
        if (items.length > 1) {
          const sorted = items.sort((a, b) => 
            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
          )
          idsToDelete.push(...sorted.slice(1).map(item => item._id!).filter(Boolean))
        }
      })
      
      if (idsToDelete.length > 0) {
        const batchSize = 50
        let deletedCount = 0
        
        for (let i = 0; i < idsToDelete.length; i += batchSize) {
          const batch = idsToDelete.slice(i, i + batchSize)
          try {
            await lumi.entities.jerarquizacion.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setJerarquizacion(prev => prev.filter(j => !idsToDelete.includes(j._id!)))
        toast.success(`${deletedCount} registros duplicados eliminados`)
      } else {
        toast.success('No se encontraron duplicados')
      }
    } catch (error: any) {
      console.error('Error al eliminar duplicados:', error)
      toast.error('Error al eliminar duplicados')
      throw error
    }
  }

  useEffect(() => {
    fetchJerarquizacion()
  }, [fetchJerarquizacion])

  return {
    data: jerarquizacion,
    loading,
    uploading,
    fetchData: fetchJerarquizacion,
    createItem: createJerarquizacion,
    updateItem: updateJerarquizacion,
    deleteItem: deleteJerarquizacion,
    uploadTXTFromURL,
    clearAllData,
    removeDuplicates
  }
}
